import { useState, useEffect } from 'react';
import { AppDateTimePicker } from '@/shared/ui/DateTimeField';
import { AppSelect } from '@/shared/ui/AppSelect';
import { toDayjs, fromDayjsISO } from '@/shared/lib/dateHelpers';
import { useThemeColors, useIsDark } from '@/shared/theme/useThemeColors';
import { typography, semantic, shadow, radius } from '@/shared/theme/tokens';

// Staged-state drawer for history filters + sort. Pure-presentational: parent owns
// the committed filters; this component edits a local staged copy and emits it on Apply.
// Renders a slide-in panel (mirrors RecommendationDrawer) — stays mounted while closing
// so the exit transition can play.

function emptyEntry(field) {
  if (field.type === 'categorical') return { type: 'categorical', value: [] };
  return { type: field.type, min: '', max: '' };
}

function seed(schema, initialFilters) {
  const out = {};
  for (const f of schema?.fields ?? []) {
    out[f.key] = initialFilters?.[f.key] ?? emptyEntry(f);
  }
  return out;
}

export function FilterDrawer({ open, onClose, schema, initialFilters, initialSort, onApply, onClear }) {
  const c = useThemeColors();
  const isDark = useIsDark();
  const [staged, setStaged] = useState(() => seed(schema, initialFilters));
  const [sort, setSort] = useState(initialSort ?? null);
  // One-tick mount flag so the enter transition fires (browser paints closed state first).
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setMounted(true), 10);
    return () => clearTimeout(id);
  }, []);

  // Re-seed whenever the drawer (re)opens or the committed inputs change.
  useEffect(() => {
    if (open) {
      setStaged(seed(schema, initialFilters));
      setSort(initialSort ?? null);
    }
  }, [open, schema, initialFilters, initialSort]);

  const shown = open && mounted;

  const setEntry = (key, patch) =>
    setStaged((s) => ({ ...s, [key]: { ...s[key], ...patch } }));

  const fieldBg = isDark ? 'rgba(255,255,255,0.04)' : c.bgSubtle;

  const inputSx = {
    width: '100%', boxSizing: 'border-box', minWidth: 0,
    fontSize: 12, fontFamily: typography.fontFamily.body, color: c.textPrimary,
    background: fieldBg,
    border: `1px solid ${c.border}`, borderRadius: 6, padding: '7px 8px', outline: 'none',
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed', inset: 0, background: semantic.overlay, zIndex: 1300,
          opacity: shown ? 1 : 0,
          transition: 'opacity 0.25s ease',
          pointerEvents: shown ? 'auto' : 'none',
        }}
      />

      {/* Panel — slides in from the right */}
      <div
        role="dialog"
        aria-label="Filters"
        style={{
          position: 'fixed', top: 0, right: 0, height: '100%', width: 380, maxWidth: '90vw',
          background: c.bg, borderLeft: `1px solid ${c.border}`, zIndex: 1301,
          display: 'flex', flexDirection: 'column', boxShadow: shadow.drawer,
          transform: shown ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.28s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {/* Header */}
        <div style={{ flexShrink: 0, padding: '16px 18px', borderBottom: `1px solid ${c.divider}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: typography.fontSize.xl, fontWeight: typography.fontWeight.bold, color: c.textPrimary, fontFamily: typography.fontFamily.body }}>Filters</span>
          <button onClick={onClose} aria-label="close" style={{ border: 'none', background: 'transparent', color: c.textMuted, cursor: 'pointer', fontSize: typography.fontSize['3xl'], lineHeight: 1 }}>×</button>
        </div>

        {/* Scrollable content — vertical only, never horizontal */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', padding: 18, display: 'flex', flexDirection: 'column', gap: 18 }}>
          {(schema?.fields ?? []).map((f) => (
            <div key={f.key} style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
              <label style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.bold, letterSpacing: typography.letterSpacing.wider, textTransform: 'uppercase', color: c.textMuted }}>{f.label}</label>

              {f.type === 'categorical' && (
                <AppSelect
                  multiple
                  size="sm"
                  ariaLabel={f.key}
                  placeholder={`Any ${f.label.toLowerCase()}`}
                  options={f.options}
                  value={staged[f.key]?.value ?? []}
                  onChange={(vals) => setEntry(f.key, { type: 'categorical', value: vals })}
                />
              )}

              {f.type === 'numeric' && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', minWidth: 0 }}>
                  <input
                    aria-label={`${f.key}-min`}
                    type="number"
                    placeholder="Min"
                    value={staged[f.key]?.min ?? ''}
                    onChange={(e) => setEntry(f.key, { min: e.target.value })}
                    style={{ ...inputSx, flex: 1 }}
                  />
                  <span style={{ color: c.textSubtle, fontSize: typography.fontSize.base, flexShrink: 0 }}>to</span>
                  <input
                    aria-label={`${f.key}-max`}
                    type="number"
                    placeholder="Max"
                    value={staged[f.key]?.max ?? ''}
                    onChange={(e) => setEntry(f.key, { max: e.target.value })}
                    style={{ ...inputSx, flex: 1 }}
                  />
                </div>
              )}

              {f.type === 'datetime' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
                  <AppDateTimePicker
                    label="Min"
                    value={toDayjs(staged[f.key]?.min)}
                    onChange={(d) => setEntry(f.key, { min: fromDayjsISO(d) })}
                    slotProps={{ textField: { inputProps: { 'aria-label': `${f.key}-min` } } }}
                  />
                  <AppDateTimePicker
                    label="Max"
                    value={toDayjs(staged[f.key]?.max)}
                    onChange={(d) => setEntry(f.key, { max: fromDayjsISO(d) })}
                    slotProps={{ textField: { inputProps: { 'aria-label': `${f.key}-max` } } }}
                  />
                </div>
              )}
            </div>
          ))}

          {(schema?.sortable?.length ?? 0) > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0, borderTop: `1px solid ${c.divider}`, paddingTop: 16 }}>
              <label style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.bold, letterSpacing: typography.letterSpacing.wider, textTransform: 'uppercase', color: c.textMuted }}>Sort</label>
              <div style={{ display: 'flex', gap: 8, minWidth: 0, alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <AppSelect
                    ariaLabel="sort-field"
                    size="sm"
                    placeholder="None"
                    searchable={false}
                    options={schema.sortable.map((s) => ({ value: s.key, label: s.label }))}
                    value={sort?.by ?? null}
                    onChange={(by) => setSort(by ? { by, dir: sort?.dir ?? 'desc' } : null)}
                  />
                </div>
                <div style={{ width: 110, flexShrink: 0 }}>
                  <AppSelect
                    ariaLabel="sort-dir"
                    size="sm"
                    clearable={false}
                    searchable={false}
                    disabled={!sort?.by}
                    options={[{ value: 'desc', label: 'Desc' }, { value: 'asc', label: 'Asc' }]}
                    value={sort?.dir ?? 'desc'}
                    onChange={(dir) => setSort((cur) => (cur?.by ? { ...cur, dir: dir ?? 'desc' } : cur))}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Sticky footer — stays pinned at the bottom while content scrolls */}
        <div style={{ flexShrink: 0, padding: 16, borderTop: `1px solid ${c.divider}`, background: c.bg, display: 'flex', gap: 10 }}>
          <button
            onClick={() => { setStaged(seed(schema, {})); setSort(null); onClear?.(); }}
            style={{ flex: 1, padding: '9px 0', borderRadius: radius.md, border: `1px solid ${c.border}`, background: 'transparent', color: c.textSubtle, cursor: 'pointer', fontSize: typography.fontSize.md, fontFamily: typography.fontFamily.body }}
          >
            Clear
          </button>
          <button
            onClick={() => onApply?.(staged, sort)}
            style={{ flex: 1, padding: '9px 0', borderRadius: radius.md, border: 'none', background: c.textActive, color: '#fff', cursor: 'pointer', fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.semibold, fontFamily: typography.fontFamily.body }}
          >
            Apply
          </button>
        </div>
      </div>
    </>
  );
}
